/**
 * Safety & Control Context
 * 
 * Provides system-wide safety configuration including:
 * - Safe Mode (emergency bypass)
 * - Validation rule CRUD (add, edit, archive, delete, reorder)
 * - Alert throttling settings
 * - Override policy settings
 * - Override audit log
 * - Rule change audit log
 * - Rule test/preview mode
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";
import { supabase } from "@/lib/supabase";
import { SafetyRulesService, OverrideLogService, type SafetyRuleRow, type OverrideLogRow } from "@/services/safety-rules.service";
import { toast } from "sonner";


/* ── Validation severity levels ── */

export type ValidationSeverityLevel = "hard_block" | "soft_block" | "informational";

export type ValidationCategory =
  | "scheduling"
  | "ordering"
  | "dispatch"
  | "job_creation"
  | "sales"
  | "pricing"
  | "compliance"
  | "insights";

export type TriggerEvent =
  | "job_creation"
  | "quote_send"
  | "quote_finalize"
  | "schedule_install"
  | "order_materials"
  | "dispatch_crew"
  | "contract_sign"
  | "payment_collect"
  | "permit_submit"
  | "general";

export interface ValidationRuleConfig {
  code: string;
  label: string;
  description: string;
  category: ValidationCategory;
  triggerEvent: TriggerEvent;
  defaultSeverity: ValidationSeverityLevel;
  currentSeverity: ValidationSeverityLevel;
  enabled: boolean;
  archived: boolean;
  isSystem: boolean; // system rules cannot be deleted
  alertEnabled: boolean;
  alertRecipientRoles: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  priority: number;
}

/* ── Override audit entry ── */

export interface OverrideAuditEntry {
  id: string;
  timestamp: string;
  ruleCode: string;
  ruleLabel: string;
  action: string;
  user: string;
  role: string;
  reason: string;
  notes: string;
  outcome: "overridden" | "blocked" | "passed";
  entityId?: string;
}

/* ── Rule change audit entry ── */

export interface RuleChangeEntry {
  id: string;
  timestamp: string;
  user: string;
  ruleCode: string;
  ruleLabel: string;
  changeType: "created" | "edited" | "enabled" | "disabled" | "archived" | "deleted" | "severity_changed" | "restored" | "priority_changed";
  before?: string;
  after?: string;
}

/* ── Alert throttle settings ── */

export interface AlertThrottleConfig {
  deduplicationWindowMinutes: number;
  maxRepeatAlerts: number;
  groupRelated: boolean;
}

/* ── Override policy ── */

export interface OverridePolicyConfig {
  overrideRoles: string[];
  requireReason: boolean;
  requireNotes: boolean;
}

/* ── Default validation rules ── */

const now = new Date().toISOString();

const DEFAULT_RULES: ValidationRuleConfig[] = [
  // ═══ SCHEDULING ═══
  { code: "SCHED_NO_CONTRACT", label: "Signed Contract Required", description: "Contract must be signed before scheduling", category: "scheduling", triggerEvent: "schedule_install", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 1 },
  { code: "SCHED_RESCISSION", label: "Rescission Period", description: "Cannot schedule during active rescission", category: "scheduling", triggerEvent: "schedule_install", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 2 },
  { code: "SCHED_NO_PERMIT", label: "Permit Approval", description: "Permit should be approved before scheduling", category: "scheduling", triggerEvent: "schedule_install", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 3 },
  { code: "SCHED_MATERIALS", label: "Material Readiness", description: "Materials should be received/staged", category: "scheduling", triggerEvent: "schedule_install", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager", "warehouse_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 4 },
  { code: "SCHED_CRITICAL_ISSUES", label: "Open Urgent Issues", description: "Urgent issues should be resolved", category: "scheduling", triggerEvent: "schedule_install", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 5 },
  { code: "SCHED_CONFLICT", label: "Scheduling Conflict", description: "Date conflicts with another job or crew", category: "scheduling", triggerEvent: "schedule_install", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 6 },

  // ═══ ORDERING ═══
  { code: "ORDER_NO_CONTRACT", label: "Contract for Ordering", description: "Cannot order without signed contract", category: "ordering", triggerEvent: "order_materials", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 1 },
  { code: "ORDER_RESCISSION", label: "Rescission for Ordering", description: "Cannot order during rescission", category: "ordering", triggerEvent: "order_materials", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 2 },
  { code: "ORDER_HOA_BLOCK", label: "HOA Not Confirmed", description: "HOA approval required before ordering", category: "ordering", triggerEvent: "order_materials", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 3 },
  { code: "ORDER_POA_BLOCK", label: "POA Not Confirmed", description: "POA documentation required before ordering", category: "ordering", triggerEvent: "order_materials", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 4 },
  { code: "ORDER_NO_MATERIALS", label: "Materials Not Ordered", description: "Materials have not been ordered yet", category: "ordering", triggerEvent: "order_materials", defaultSeverity: "informational", currentSeverity: "informational", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 5 },

  // ═══ DISPATCH ═══
  { code: "DISP_UNASSIGNED", label: "Crew Assignment", description: "Must have crew/subcontractor assigned", category: "dispatch", triggerEvent: "dispatch_crew", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 1 },
  { code: "DISP_NO_DATE", label: "Install Date", description: "Must have scheduled install date", category: "dispatch", triggerEvent: "dispatch_crew", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 2 },
  { code: "DISP_PERMIT", label: "Permit for Dispatch", description: "Permit must be approved for dispatch", category: "dispatch", triggerEvent: "dispatch_crew", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 3 },
  { code: "DISP_MATERIALS", label: "Materials for Dispatch", description: "Materials should be staged/loaded", category: "dispatch", triggerEvent: "dispatch_crew", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["warehouse_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 4 },
  { code: "DISP_URGENT_ISSUES", label: "Urgent Issues for Dispatch", description: "Urgent issues block dispatch", category: "dispatch", triggerEvent: "dispatch_crew", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 5 },

  // ═══ JOB CREATION ═══
  { code: "JOB_NOT_WON", label: "Quote Must Be Won", description: "Quote must be Won to create job", category: "job_creation", triggerEvent: "job_creation", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 1 },
  { code: "JOB_NO_CONTRACT", label: "Contract for Job", description: "Contract must be signed", category: "job_creation", triggerEvent: "job_creation", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 2 },
  { code: "JOB_NO_VERIFY", label: "Customer Verification", description: "Customer verification should be complete", category: "job_creation", triggerEvent: "job_creation", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 3 },
  { code: "JOB_ALREADY_EXISTS", label: "Duplicate Job Check", description: "Job already exists for this quote", category: "job_creation", triggerEvent: "job_creation", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 4 },

  // ═══ SALES ═══
  { code: "SALE_NO_CUSTOMER_NAME", label: "Customer Name Required", description: "Customer name is required to proceed", category: "sales", triggerEvent: "quote_finalize", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 1 },
  { code: "SALE_NO_PHONE", label: "Phone Number Required", description: "Customer phone is required to proceed", category: "sales", triggerEvent: "quote_finalize", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 2 },
  { code: "SALE_NO_PAYMENT_METHOD", label: "Payment Method Required", description: "A payment method is required to finalize the deal", category: "sales", triggerEvent: "quote_finalize", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 3 },
  { code: "SALE_INVALID_DEPOSIT", label: "Invalid Deposit Amount", description: "Non-financing deals require a deposit greater than $0", category: "sales", triggerEvent: "quote_finalize", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 4 },
  { code: "SALE_MISSING_ADDRESS", label: "Address Verification", description: "Address verification is incomplete", category: "sales", triggerEvent: "quote_finalize", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 5 },
  { code: "SALE_NO_NOTES", label: "Internal Notes Missing", description: "No internal notes have been added to the quote", category: "sales", triggerEvent: "quote_finalize", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 6 },
  { code: "SALE_HOA_NOT_CONFIRMED", label: "HOA Status Unknown", description: "HOA involvement has not been confirmed", category: "sales", triggerEvent: "quote_finalize", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 7 },
  { code: "SALE_POA_MISSING_DOC", label: "POA Document Missing", description: "POA selected but no document uploaded", category: "compliance", triggerEvent: "quote_finalize", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 1 },

  // ═══ PRICING ═══
  { code: "PRICE_BELOW_FLOOR", label: "Below Floor Price", description: "Quote is priced below the minimum acceptable floor", category: "pricing", triggerEvent: "quote_finalize", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["owner", "cfo"], createdAt: now, updatedAt: now, createdBy: "System", priority: 1 },
  { code: "PRICE_BELOW_TARGET", label: "Below Target Price", description: "Quote is below target but above floor", category: "pricing", triggerEvent: "quote_finalize", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["owner"], createdAt: now, updatedAt: now, createdBy: "System", priority: 2 },
  { code: "PRICE_MANUAL_OVERRIDE", label: "Manual Price Override", description: "A manual pricing adjustment has been applied", category: "pricing", triggerEvent: "quote_finalize", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 3 },
  { code: "PRICE_LARGE_DISCOUNT", label: "Large Discount Applied", description: "Discount exceeds 15% of calculated price", category: "pricing", triggerEvent: "quote_finalize", defaultSeverity: "soft_block", currentSeverity: "soft_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["owner"], createdAt: now, updatedAt: now, createdBy: "System", priority: 4 },
  { code: "PRICE_NO_GUARDRAILS", label: "Guardrails Not Set", description: "Pricing guardrails have not been configured", category: "pricing", triggerEvent: "quote_finalize", defaultSeverity: "informational", currentSeverity: "informational", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 5 },

  // ═══ COMPLIANCE ═══
  { code: "COMP_MISSING_DISCLOSURE", label: "Disclosure Missing", description: "Required disclosure documents are not attached", category: "compliance", triggerEvent: "contract_sign", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager"], createdAt: now, updatedAt: now, createdBy: "System", priority: 2 },
  { code: "COMP_BROKEN_LINK", label: "Broken Data Relationship", description: "Invalid or broken link between contact, quote, and job", category: "compliance", triggerEvent: "general", defaultSeverity: "hard_block", currentSeverity: "hard_block", enabled: true, archived: false, isSystem: true, alertEnabled: true, alertRecipientRoles: ["ops_manager", "admin"], createdAt: now, updatedAt: now, createdBy: "System", priority: 3 },

  // ═══ INSIGHTS ═══
  { code: "INFO_UPSELL", label: "Upsell Opportunity", description: "Recommended add-ons or upgrades available", category: "insights", triggerEvent: "quote_send", defaultSeverity: "informational", currentSeverity: "informational", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 1 },
  { code: "INFO_ALT_MATERIALS", label: "Alternative Materials", description: "Cost-effective or faster-delivery alternatives exist", category: "insights", triggerEvent: "order_materials", defaultSeverity: "informational", currentSeverity: "informational", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 2 },
  { code: "INFO_LOW_INVENTORY", label: "Low Inventory Notice", description: "Selected materials are running low in stock", category: "insights", triggerEvent: "order_materials", defaultSeverity: "informational", currentSeverity: "informational", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 3 },
  { code: "INFO_CONTRACTOR_PERF", label: "Contractor Performance", description: "Assigned contractor has a low reliability score", category: "insights", triggerEvent: "dispatch_crew", defaultSeverity: "informational", currentSeverity: "informational", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 4 },
  { code: "INFO_LEAD_QUALITY", label: "Lead Source Quality", description: "Lead source has a high rejection rate", category: "insights", triggerEvent: "general", defaultSeverity: "informational", currentSeverity: "informational", enabled: true, archived: false, isSystem: true, alertEnabled: false, alertRecipientRoles: [], createdAt: now, updatedAt: now, createdBy: "System", priority: 5 },
];

/* ── Context value ── */

interface SafetyControlValue {
  safeMode: boolean;
  setSafeMode: (on: boolean) => void;
  rules: ValidationRuleConfig[];
  updateRuleSeverity: (code: string, severity: ValidationSeverityLevel) => void;
  toggleRule: (code: string) => void;
  resetRules: () => void;
  addRule: (rule: Omit<ValidationRuleConfig, "createdAt" | "updatedAt" | "priority">) => void;
  editRule: (code: string, updates: Partial<ValidationRuleConfig>) => void;
  archiveRule: (code: string) => void;
  restoreRule: (code: string) => void;
  deleteRule: (code: string) => boolean;
  duplicateRule: (code: string) => void;
  overrideLog: OverrideAuditEntry[];
  logOverride: (entry: Omit<OverrideAuditEntry, "id" | "timestamp">) => void;
  ruleChangeLog: RuleChangeEntry[];
  alertThrottle: AlertThrottleConfig;
  setAlertThrottle: (config: AlertThrottleConfig) => void;
  overridePolicy: OverridePolicyConfig;
  setOverridePolicy: (config: OverridePolicyConfig) => void;
  getEffectiveSeverity: (code: string) => ValidationSeverityLevel;
  isRuleEnabled: (code: string) => boolean;
}

const SafetyControlContext = createContext<SafetyControlValue | null>(null);

export function SafetyControlProvider({ children }: { children: ReactNode }) {
  const [safeMode, setSafeMode] = useState(false);
  const [rules, setRules] = useState<ValidationRuleConfig[]>(DEFAULT_RULES);
  const [overrideLog, setOverrideLog] = useState<OverrideAuditEntry[]>([]);
  const [ruleChangeLog, setRuleChangeLog] = useState<RuleChangeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();
  const [alertThrottle, setAlertThrottle] = useState<AlertThrottleConfig>({
    deduplicationWindowMinutes: 30,
    maxRepeatAlerts: 3,
    groupRelated: true,
  });
  const [overridePolicy, setOverridePolicy] = useState<OverridePolicyConfig>({
    overrideRoles: [],
    requireReason: true,
    requireNotes: false,
  });

  // Log rule changes to the change log
  const logRuleChange = useCallback((entry: Omit<RuleChangeEntry, "id" | "timestamp">) => {
    const full: RuleChangeEntry = {
      ...entry,
      id: `rcl-${Date.now().toString(36).slice(-4)}`,
      timestamp: new Date().toISOString(),
    };
    setRuleChangeLog(prev => [full, ...prev]);
  }, []);

  // Load rules from DB on mount
  useEffect(() => {
    if (deferHeavyProvider) {
      setLoading(false);
      return;
    }

    async function loadRules() {
      try {
        const { data: dbRules, error } = await SafetyRulesService.getAll();
        if (dbRules && dbRules.length > 0) {
          // Map DB rules to ValidationRuleConfig format
          const mappedRules: ValidationRuleConfig[] = dbRules.map((r: SafetyRuleRow) => ({
            code: r.code,
            label: r.name,
            description: r.description || "",
            category: mapCategory(r.category),
            triggerEvent: "general",
            defaultSeverity: mapSeverity(r.severity),
            currentSeverity: mapSeverity(r.severity),
            enabled: r.is_active ?? true,
            archived: !(r.is_active ?? true),
            isSystem: true,
            alertEnabled: true,
            alertRecipientRoles: [],
            createdAt: r.created_at || now,
            updatedAt: r.updated_at || now,
            createdBy: "System",
            priority: r.order_index || 0,
          }));
          setRules(mappedRules);
        }
        
        // Load override log from DB
        const { data: logs } = await OverrideLogService.getAll();
        if (logs) {
          const mappedLogs: OverrideAuditEntry[] = logs.map((l: OverrideLogRow) => ({
            id: l.id || "",
            timestamp: l.created_at || "",
            ruleCode: l.rule_code,
            ruleLabel: l.rule_code,
            action: "override",
            user: l.user_id,
            role: l.role || "",
            reason: l.override_reason || "",
            notes: "",
            outcome: l.outcome as "overridden" | "blocked" | "passed",
          }));
          setOverrideLog(mappedLogs);
        }
      } catch (err) {
        console.error("[SafetyControl] Failed to load rules:", err);
      } finally {
        setLoading(false);
      }
    }
    loadRules();
  }, [deferHeavyProvider]);

  // Helper functions to map DB values to context format
  function mapCategory(cat: string): ValidationCategory {
    const raw = (cat || "").trim().toLowerCase();
    const normalized = raw.replace(/\s+/g, "_");
    const map: Record<string, ValidationCategory> = {
      scheduling: "scheduling",
      ordering: "ordering",
      dispatch: "dispatch",
      job_creation: "job_creation",
      sales: "sales",
      pricing: "pricing",
      compliance: "compliance",
      insights: "insights",
    };
    return map[normalized] || "compliance";
  }

  function mapCategoryToDb(cat: ValidationCategory): string {
    return cat;
  }

  function mapSeverity(sev: string): ValidationSeverityLevel {
    const s = (sev || "").trim().toLowerCase();
    if (s === "block" || s === "hard_block") return "hard_block";
    if (s === "warning" || s === "soft_block") return "soft_block";
    return "informational";
  }

  function mapSeverityToDb(sev: ValidationSeverityLevel): string {
    if (sev === "hard_block") return "hard_block";
    if (sev === "soft_block") return "soft_block";
    return "informational";
  }

  // Update rule to persist to DB
  const updateRuleSeverity = useCallback(async (code: string, severity: ValidationSeverityLevel) => {
    setRules(prev => {
      const rule = prev.find(r => r.code === code);
      if (rule) {
        logRuleChange({ user: "Current User", ruleCode: code, ruleLabel: rule.label, changeType: "severity_changed", before: rule.currentSeverity, after: severity });
        // Persist to DB
        SafetyRulesService.updateSeverity(code, mapSeverityToDb(severity)).catch(err => {
          console.error(err);
          toast.error("Failed to update severity: " + err.message);
        });
      }
      return prev.map(r => r.code === code ? { ...r, currentSeverity: severity, updatedAt: new Date().toISOString() } : r);
    });
  }, []);

  const toggleRule = useCallback(async (code: string) => {
    setRules(prev => {
      const rule = prev.find(r => r.code === code);
      if (rule) {
        const newEnabled = !rule.enabled;
        logRuleChange({ user: "Current User", ruleCode: code, ruleLabel: rule.label, changeType: newEnabled ? "enabled" : "disabled" });
        // Persist to DB
        SafetyRulesService.toggleActive(code, newEnabled).catch(err => {
          console.error(err);
          toast.error("Failed to toggle rule: " + err.message);
        });
        return prev.map(r => r.code === code ? { ...r, enabled: newEnabled, archived: !newEnabled, updatedAt: new Date().toISOString() } : r);
      }
      return prev;
    });
  }, []);

  const resetRules = useCallback(async () => {
    setRules(DEFAULT_RULES);
  }, []);

  const addRule = useCallback(async (rule: Omit<ValidationRuleConfig, "createdAt" | "updatedAt" | "priority">) => {
    const nowStr = new Date().toISOString();
    const maxPriority = rules.filter(r => r.category === rule.category).length + 1;
    const full: ValidationRuleConfig = { ...rule, createdAt: nowStr, updatedAt: nowStr, priority: maxPriority };
    setRules(prev => [...prev, full]);
    logRuleChange({ user: "Current User", ruleCode: rule.code, ruleLabel: rule.label, changeType: "created" });
    // Persist to DB
    await SafetyRulesService.create({
      code: rule.code,
      name: rule.label,
      description: rule.description,
      category: mapCategoryToDb(rule.category),
      severity: mapSeverityToDb(rule.currentSeverity),
      is_active: rule.enabled,
      order_index: maxPriority,
    });
  }, [rules]);

  const editRule = useCallback(async (code: string, updates: Partial<ValidationRuleConfig>) => {
    setRules(prev => {
      const rule = prev.find(r => r.code === code);
      if (rule) {
        logRuleChange({ user: "Current User", ruleCode: code, ruleLabel: rule.label, changeType: "edited", before: JSON.stringify({ label: rule.label, description: rule.description }), after: JSON.stringify(updates) });
        // Persist to DB
        SafetyRulesService.update(code, {
          name: updates.label,
          description: updates.description,
          severity: updates.currentSeverity ? mapSeverityToDb(updates.currentSeverity) : undefined,
        }).catch(err => {
          console.error(err);
          toast.error("Failed to update rule: " + err.message);
        });
      }
      return prev.map(r => r.code === code ? { ...r, ...updates, updatedAt: new Date().toISOString() } : r);
    });
  }, []);

  const archiveRule = useCallback(async (code: string) => {
    setRules(prev => {
      const rule = prev.find(r => r.code === code);
      if (rule) {
        logRuleChange({ user: "Current User", ruleCode: code, ruleLabel: rule.label, changeType: "archived" });
        // Persist to DB
        SafetyRulesService.archive(code).catch(err => {
          console.error(err);
          toast.error("Failed to archive rule: " + err.message);
        });
        return prev.map(r => r.code === code ? { ...r, archived: true, enabled: false, updatedAt: new Date().toISOString() } : r);
      }
      return prev;
    });
  }, []);

  const restoreRule = useCallback(async (code: string) => {
    setRules(prev => {
      const rule = prev.find(r => r.code === code);
      if (rule) {
        logRuleChange({ user: "Current User", ruleCode: code, ruleLabel: rule.label, changeType: "restored" });
        // Persist to DB
        SafetyRulesService.toggleActive(code, true).catch(err => {
          console.error(err);
          toast.error("Failed to restore rule: " + err.message);
        });
        return prev.map(r => r.code === code ? { ...r, archived: false, enabled: true, updatedAt: new Date().toISOString() } : r);
      }
      return prev;
    });
  }, []);

  const deleteRule = useCallback(async (code: string): boolean => {
    const rule = rules.find(r => r.code === code);
    if (!rule || rule.isSystem) return false;
    logRuleChange({ user: "Current User", ruleCode: code, ruleLabel: rule.label, changeType: "deleted" });
    setRules(prev => prev.filter(r => r.code !== code));
    // Persist to DB
    await SafetyRulesService.delete(code);
    return true;
  }, [rules]);

  const duplicateRule = useCallback(async (code: string) => {
    const rule = rules.find(r => r.code === code);
    if (!rule) return;
    const nowStr = new Date().toISOString();
    const newCode = `${code}-copy-${Date.now().toString(36).slice(-4)}`;
    const newRule: ValidationRuleConfig = {
      ...rule,
      code: newCode,
      label: `${rule.label} (Copy)`,
      enabled: false,
      archived: false,
      createdAt: nowStr,
      updatedAt: nowStr,
      priority: rules.filter(r => r.category === rule.category).length + 1,
    };
    setRules(prev => [...prev, newRule]);
    logRuleChange({ user: "Current User", ruleCode: newCode, ruleLabel: newRule.label, changeType: "created" });
    // Persist to DB
    await SafetyRulesService.create({
      code: newCode,
      name: newRule.label,
      description: newRule.description,
      category: mapCategoryToDb(newRule.category),
      severity: mapSeverityToDb(newRule.currentSeverity),
      is_active: newRule.enabled,
      order_index: newRule.priority,
    });
  }, [rules]);

  const logOverride = useCallback(async (entry: Omit<OverrideAuditEntry, "id" | "timestamp">) => {
    const full: OverrideAuditEntry = {
      ...entry,
      id: `ovr-${Date.now().toString(36).slice(-4)}`,
      timestamp: new Date().toISOString(),
    };
    setOverrideLog(prev => [full, ...prev]);
    // Persist to DB
    await OverrideLogService.create({
      rule_code: entry.ruleCode,
      user_id: entry.user,
      role: entry.role,
      outcome: entry.outcome,
      override_reason: entry.reason,
    });
  }, []);

  const getEffectiveSeverity = useCallback((code: string): ValidationSeverityLevel => {
    if (safeMode) return "informational";
    const rule = rules.find(r => r.code === code);
    if (!rule || !rule.enabled || rule.archived) return "informational";
    return rule.currentSeverity;
  }, [safeMode, rules]);

  const isRuleEnabled = useCallback((code: string): boolean => {
    if (safeMode) return true;
    const rule = rules.find(r => r.code === code);
    if (!rule || rule.archived) return false;
    return rule.enabled;
  }, [safeMode, rules]);

  return (
    <SafetyControlContext.Provider value={{
      safeMode, setSafeMode,
      rules, updateRuleSeverity, toggleRule, resetRules,
      addRule, editRule, archiveRule, restoreRule, deleteRule, duplicateRule,
      overrideLog, logOverride,
      ruleChangeLog,
      alertThrottle, setAlertThrottle,
      overridePolicy, setOverridePolicy,
      getEffectiveSeverity, isRuleEnabled,
    }}>
      {children}
    </SafetyControlContext.Provider>
  );
}

export function useSafetyControl() {
  const ctx = useContext(SafetyControlContext);
  if (!ctx) throw new Error("useSafetyControl must be used within SafetyControlProvider");
  return ctx;
}

/* ── Constants for external use ── */
export const CATEGORY_OPTIONS: { value: ValidationCategory; label: string }[] = [
  { value: "scheduling", label: "Scheduling" },
  { value: "ordering", label: "Ordering" },
  { value: "dispatch", label: "Dispatch" },
  { value: "job_creation", label: "Job Creation" },
  { value: "sales", label: "Sales & Quotes" },
  { value: "pricing", label: "Pricing" },
  { value: "compliance", label: "Compliance" },
  { value: "insights", label: "Insights" },
];

export const TRIGGER_OPTIONS: { value: TriggerEvent; label: string }[] = [
  { value: "job_creation", label: "Job Creation" },
  { value: "quote_send", label: "Quote Send" },
  { value: "quote_finalize", label: "Quote Finalize" },
  { value: "schedule_install", label: "Schedule Install" },
  { value: "order_materials", label: "Order Materials" },
  { value: "dispatch_crew", label: "Dispatch Crew" },
  { value: "contract_sign", label: "Contract Sign" },
  { value: "payment_collect", label: "Payment Collect" },
  { value: "permit_submit", label: "Permit Submit" },
  { value: "general", label: "General" },
];
